import { useEffect } from 'react'
import { startNewLabraChat } from './actions'
import { useLabraChatDispatch, useLabraChatSelector } from './context'

/**
 * The last chat page that was on screen. Survives unmount so a new page's
 * shell can see that the user came from somewhere else. Not cleared when a
 * non-chat route is shown, so returning to the same page keeps that chat.
 */
let activeChatPageKey: string | undefined

/** Test helper. Production callers leave this alone. */
export const resetActiveChatPageKey = () => {
  activeChatPageKey = undefined
}

/**
 * True when the user moved from one chat page to another while a conversation
 * was already open. The first page they land on, and a return to the same
 * page, do not start a new chat.
 */
export const shouldStartFreshChat = (
  pageKey: string,
  conversation: 'open' | 'empty'
): boolean => {
  const previous = activeChatPageKey
  activeChatPageKey = pageKey
  return (
    previous !== undefined && previous !== pageKey && conversation === 'open'
  )
}

/**
 * Start a new chat when the user opens a different chat page with a
 * conversation already going. The next message then carries that page's
 * context. An empty chat is left alone, because nothing has been sent yet.
 */
export const useFreshChatOnPageChange = (pageKey: string) => {
  const dispatch = useLabraChatDispatch()
  const hasConversation = useLabraChatSelector(
    state =>
      (state.labraChat?.messages.length ?? 0) > 0 ||
      Boolean(state.labraChat?.sessionId)
  )

  useEffect(() => {
    if (shouldStartFreshChat(pageKey, hasConversation ? 'open' : 'empty')) {
      dispatch(startNewLabraChat())
    }
  }, [pageKey, hasConversation, dispatch])
}

/** Renders nothing. Mount it inside a chat page so a page change can start fresh. */
export const FreshChatOnPageChange = ({ pageKey }: { pageKey: string }) => {
  useFreshChatOnPageChange(pageKey)
  return null
}
